import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  QUADROS_POR_SEGUNDO_QUEDA,
  resumirQueda,
  simularQuedaAnimada,
  type ResumoQueda,
  type CenaTabletop,
  type QuadroQueda,
  type ResultadoQuedaAnimada,
} from '../../dominio/tabletop'
import { carregarMotor, type EstadoMotor } from '../../fisica/motor'
import { simularQuedaComFisica, simularQuedaLivreDoTrabalhador } from '../../fisica/quedaComFisica'

export interface QuedaAnimadaNaCena {
  readonly resultado: ResultadoQuedaAnimada
  readonly indice: number
  readonly quadro: QuadroQueda
}

export interface QuedaAnimadaTabletop {
  /** A queda simulada, quando há uma. */
  readonly resultado: ResultadoQuedaAnimada | null
  readonly indice: number
  readonly tocando: boolean
  /** O quadro da régua de tempo, para a cena desenhar. */
  readonly naCena: QuedaAnimadaNaCena | null
  /** A queda guardada para comparar. Sobrevive às mudanças na cena: é para mexer em uma coisa e simular de novo. */
  readonly referencia: ResumoQueda | null
  guardarReferencia: () => void
  limparReferencia: () => void
  /** O motor de física carrega só quando se pede a simulação com física. */
  readonly estadoMotor: EstadoMotor
  simular: (idCorda: string) => void
  /** A mesma queda, simulada com o motor de física (o cálculo vertical fica como comparação). */
  simularComFisica: (idCorda: string) => Promise<void>
  /** Queda livre de um trabalhador no ar (sem talabarte ou solto). */
  simularQuedaLivre: (idTrabalhador: string) => Promise<void>
  alternarReproducao: () => void
  definirIndice: (indice: number) => void
  limpar: () => void
}

const SEM_QUEDA: QuedaAnimadaTabletop = {
  resultado: null,
  indice: 0,
  tocando: false,
  naCena: null,
  referencia: null,
  guardarReferencia: () => {},
  limparReferencia: () => {},
  estadoMotor: 'ocioso',
  simular: () => {},
  simularComFisica: async () => {},
  simularQuedaLivre: async () => {},
  alternarReproducao: () => {},
  definirIndice: () => {},
  limpar: () => {},
}

const ContextoQueda = createContext<QuedaAnimadaTabletop>(SEM_QUEDA)
export const useQuedaAnimada = () => useContext(ContextoQueda)

export function ProvedorQuedaAnimada({ valor, children }: { valor: QuedaAnimadaTabletop; children: ReactNode }) {
  return <ContextoQueda.Provider value={valor}>{children}</ContextoQueda.Provider>
}

/**
 * A queda animada do trabalhador: roda no domínio (sem motor de física, nada a
 * carregar) e vale só para a montagem em que foi feita. Mexeu em qualquer coisa
 * da cena, ela some e se refaz. A régua de tempo anda nos dois sentidos.
 */
export function useQuedaAnimadaTabletop(cena: CenaTabletop): QuedaAnimadaTabletop {
  const [resultado, setResultado] = useState<ResultadoQuedaAnimada | null>(null)
  const [indice, setIndice] = useState(0)
  const [tocando, setTocando] = useState(false)
  const [referencia, setReferencia] = useState<ResumoQueda | null>(null)
  const [estadoMotor, setEstadoMotor] = useState<EstadoMotor>('ocioso')
  const cenaDaQueda = useRef<CenaTabletop | null>(null)
  const cenaAtual = useRef(cena)
  useEffect(() => {
    cenaAtual.current = cena
  }, [cena])

  useEffect(() => {
    if (cenaDaQueda.current && cenaDaQueda.current !== cena) {
      cenaDaQueda.current = null
      setResultado(null)
      setTocando(false)
      setIndice(0)
    }
  }, [cena])

  const simular = useCallback(
    (idCorda: string) => {
      const nova = simularQuedaAnimada(cena, idCorda)
      cenaDaQueda.current = cena
      setResultado(nova)
      setIndice(0)
      // só começa a tocar quando há quadros: sem queda não há o que ver
      setTocando(Boolean(nova && nova.quadros.length > 1))
    },
    [cena],
  )

  const simularComFisica = useCallback(
    async (idCorda: string) => {
      setEstadoMotor((atual) => (atual === 'pronto' ? atual : 'carregando'))
      try {
        const R = await carregarMotor()
        setEstadoMotor('pronto')
        // mexeram na cena enquanto o motor carregava: a queda valeria para outra montagem
        if (cenaAtual.current !== cena) return
        const nova = simularQuedaComFisica(R, cena, idCorda)
        cenaDaQueda.current = cena
        setResultado(nova)
        setIndice(0)
        setTocando(Boolean(nova && nova.quadros.length > 1))
      } catch {
        setEstadoMotor('indisponivel')
      }
    },
    [cena],
  )

  const simularQuedaLivre = useCallback(
    async (idTrabalhador: string) => {
      setEstadoMotor((atual) => (atual === 'pronto' ? atual : 'carregando'))
      try {
        const R = await carregarMotor()
        setEstadoMotor('pronto')
        if (cenaAtual.current !== cena) return
        const nova = simularQuedaLivreDoTrabalhador(R, cena, idTrabalhador)
        cenaDaQueda.current = cena
        setResultado(nova)
        setIndice(0)
        setTocando(Boolean(nova && nova.quadros.length > 1))
      } catch {
        setEstadoMotor('indisponivel')
      }
    },
    [cena],
  )

  const guardarReferencia = useCallback(() => {
    if (resultado) setReferencia(resumirQueda(cena, resultado))
  }, [cena, resultado])
  const limparReferencia = useCallback(() => setReferencia(null), [])

  const quadros = resultado?.quadros.length ?? 0
  useEffect(() => {
    if (!tocando || quadros === 0) return
    const intervalo = window.setInterval(() => {
      setIndice((atual) => {
        if (atual >= quadros - 1) {
          setTocando(false)
          return atual
        }
        return atual + 1
      })
    }, 1000 / QUADROS_POR_SEGUNDO_QUEDA)
    return () => window.clearInterval(intervalo)
  }, [tocando, quadros])

  const definirIndice = useCallback((novo: number) => {
    setTocando(false)
    setIndice(novo)
  }, [])
  const alternarReproducao = useCallback(() => {
    setTocando((atual) => {
      if (!atual) setIndice((i) => (i >= quadros - 1 ? 0 : i))
      return !atual
    })
  }, [quadros])
  const limpar = useCallback(() => {
    cenaDaQueda.current = null
    setResultado(null)
    setTocando(false)
    setIndice(0)
  }, [])

  const naCena = useMemo<QuedaAnimadaNaCena | null>(() => {
    if (!resultado || quadros === 0) return null
    const i = Math.min(indice, quadros - 1)
    return { resultado, indice: i, quadro: resultado.quadros[i]! }
  }, [resultado, indice, quadros])

  return useMemo(
    () => ({
      resultado,
      indice,
      tocando,
      naCena,
      referencia,
      guardarReferencia,
      limparReferencia,
      estadoMotor,
      simular,
      simularComFisica,
      simularQuedaLivre,
      alternarReproducao,
      definirIndice,
      limpar,
    }),
    [
      alternarReproducao,
      definirIndice,
      estadoMotor,
      guardarReferencia,
      indice,
      limpar,
      limparReferencia,
      naCena,
      referencia,
      resultado,
      simular,
      simularComFisica,
      simularQuedaLivre,
      tocando,
    ],
  )
}
